/**
 * TMS-7 through TMS-12 seed data.
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const TENANT = DEMO_TENANT_ID;
const today = () => new Date().toISOString().slice(0, 10);
const iso = (days = 0, hours = 0) => new Date(Date.now() - (days * 86400000 + hours * 3600000)).toISOString();
const plus = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);

// ── TMS-7 Carrier Allocation Rules ────────────────────────────────────
export const SEED_CARRIER_ALLOCATION_RULES = [
  {
    id: 'alloc-rr-il', tenantId: TENANT, customerId: 'cust-riverside',
    carrierScac: 'XPOL', laneName: 'Romeoville Lane',
    originRegion: 'IL', destinationRegion: 'IL',
    maxLoadsPerDay: 3, currentDayCount: 2, priority: 1,
    active: true, lastResetDate: today(), createdAt: iso(14),
  },
  {
    id: 'alloc-rr-il-2', tenantId: TENANT, customerId: 'cust-riverside',
    carrierScac: 'SAIA', laneName: 'Romeoville Lane',
    originRegion: 'IL', destinationRegion: 'IL',
    maxLoadsPerDay: 3, currentDayCount: 0, priority: 2,
    active: true, lastResetDate: today(), createdAt: iso(14),
  },
  {
    id: 'alloc-tx-ca', tenantId: TENANT,
    carrierScac: 'ODFL', laneName: 'TX → CA Lane',
    originRegion: 'TX', destinationRegion: 'CA',
    maxLoadsPerDay: 5, currentDayCount: 3, priority: 1,
    active: true, lastResetDate: today(), createdAt: iso(30),
  },
];

// ── TMS-7 Report Schedules + Runs ─────────────────────────────────────
export const SEED_REPORT_SCHEDULES = [
  {
    id: 'rs-0001', tenantId: TENANT,
    name: 'Daily missing delivery appointments',
    reportType: 'loads_without_delivery_appt',
    schedule: { frequency: 'daily', timeHHMM: '08:00' },
    recipients: ['dispatcher@kairos-demo.ai', 'michelle.bero@riversidefoods.com'],
    format: 'email',
    active: true,
    nextRun: plus(1) + 'T13:00:00Z',
    lastRun: iso(1),
    createdBy: 'admin',
  },
  {
    id: 'rs-0002', tenantId: TENANT,
    name: 'Weekly carrier performance',
    reportType: 'carrier_performance',
    schedule: { frequency: 'weekly', dayOfWeek: 1, timeHHMM: '09:00' },
    recipients: ['executive@kairos-demo.ai'],
    format: 'pdf',
    active: true,
    nextRun: plus(7) + 'T14:00:00Z',
    lastRun: iso(7),
    createdBy: 'admin',
  },
];

export const SEED_REPORT_RUNS = [
  {
    id: 'rr-0001', tenantId: TENANT, scheduleId: 'rs-0001',
    reportType: 'loads_without_delivery_appt',
    runAt: iso(1), recipientCount: 2, rowCount: 7,
    narrative: 'AEGIS: 7 loads are missing delivery appointments, up from 3 last week. 4 are on the Romeoville lane with XPO. Recommend prioritizing XPO contact today — appointment rate declined 18% over 30 days.',
    status: 'success',
  },
];

// ── TMS-7 EDI Messages ────────────────────────────────────────────────
export const SEED_EDI_MESSAGES = [
  { id: 'edi-0001', tenantId: TENANT, messageType: '214', loadId: 'load-00001', statusCode: 'AF', location: 'Chicago, IL', occurredAt: iso(0, 8), appliedAt: iso(0, 7), parsed: { statusCode: 'AF', label: 'Picked Up' }, source: 'webhook' },
  { id: 'edi-0002', tenantId: TENANT, messageType: '214', loadId: 'load-00001', statusCode: 'X1', location: 'Chicago, IL', occurredAt: iso(0, 6), appliedAt: iso(0, 6), parsed: { statusCode: 'X1', label: 'Departed Pickup' }, source: 'webhook' },
  { id: 'edi-0003', tenantId: TENANT, messageType: '214', loadId: 'load-00002', statusCode: 'A9', location: 'I-80 MP 210', occurredAt: iso(0, 3), appliedAt: iso(0, 3), parsed: { statusCode: 'A9', label: 'ETA Changed' }, eta: iso(-1), source: 'webhook' },
  { id: 'edi-0004', tenantId: TENANT, messageType: '990', loadId: 'load-00009', statusCode: 'CA', appliedAt: iso(0, 10), parsed: { statusCode: 'CA', label: 'Accepted' }, source: 'webhook' },
  { id: 'edi-0005', tenantId: TENANT, messageType: '990', loadId: 'load-00011', statusCode: 'CD', appliedAt: iso(0, 11), parsed: { statusCode: 'CD', label: 'Declined' }, source: 'webhook' },
];

// ── TMS-8 Carrier Onboarding Applications ─────────────────────────────
export const SEED_CARRIER_ONBOARDING_APPLICATIONS = [
  {
    id: 'app-0001', tenantId: TENANT, companyName: 'Northwood Express',
    scac: 'NWXP', dotNumber: '3420911', mcNumber: 'MC-923411',
    contactName: 'Dana Park', contactEmail: 'dana@northwoodexpress.com',
    status: 'submitted', completedSteps: ['profile', 'authority', 'insurance', 'w9', 'tax', 'packet'],
    insuranceExpiry: plus(365), safetyScore: 88,
    createdAt: iso(2),
  },
  {
    id: 'app-0002', tenantId: TENANT, companyName: 'Lonestar Direct',
    scac: 'LSDR', dotNumber: '4211872', mcNumber: 'MC-3091222',
    contactName: 'Ben Ruiz', contactEmail: 'ben@lonestardirect.com',
    status: 'in_progress', completedSteps: ['profile', 'authority'],
    createdAt: iso(1),
  },
];

// ── TMS-9 Load Board Postings & Quotes ────────────────────────────────
export const SEED_LOADBOARD_POSTINGS = [
  {
    id: 'post-0001', tenantId: TENANT, loadId: 'load-00030',
    origin: 'Chicago, IL', destination: 'Dallas, TX',
    weight: 44000, mode: 'FTL', askingRate: 2600,
    targetBoard: 'both', status: 'live',
    externalIds: { dat: 'DAT-MOCK-004123', truckstop: 'TS-MOCK-007829' },
    postedAt: iso(0, 4), postedBy: 'dispatcher', source: 'kairos', mockMode: true,
  },
  {
    id: 'post-0002', tenantId: TENANT, loadId: 'load-00031',
    origin: 'Atlanta, GA', destination: 'Newark, NJ',
    weight: 38000, mode: 'FTL', askingRate: 2380,
    targetBoard: 'dat', status: 'live',
    externalIds: { dat: 'DAT-MOCK-004187' },
    postedAt: iso(0, 2), postedBy: 'dispatcher', source: 'kairos', mockMode: true,
  },
];

export const SEED_LOADBOARD_QUOTES = [
  { id: 'lbq-0001', tenantId: TENANT, postingId: 'post-0001', carrierName: 'TransWest Carriers', carrierScac: 'TWCR', rate: 2480, equipment: 'Dry Van', transitDays: 2, receivedAt: iso(0, 3), source: 'mock' },
  { id: 'lbq-0002', tenantId: TENANT, postingId: 'post-0001', carrierName: 'Rocksolid Freight', carrierScac: 'RSFT', rate: 2540, equipment: 'Dry Van', transitDays: 2, receivedAt: iso(0, 2), source: 'mock' },
  { id: 'lbq-0003', tenantId: TENANT, postingId: 'post-0002', carrierName: 'NE Express', carrierScac: 'NEXP', rate: 2290, equipment: 'Dry Van', transitDays: 3, receivedAt: iso(0, 1), source: 'mock' },
];

// ── TMS-10 Rate Tables + Contracts ────────────────────────────────────
export const SEED_CARRIER_RATE_TABLES = [
  { id: 'rt-0001', tenantId: TENANT, carrierId: 'carrier-od', carrierName: 'Old Dominion', scac: 'ODFL',   origin: 'Chicago, IL', destination: 'Dallas, TX',   mode: 'FTL',   rate: 2600, minWeight: 20000, fuelSurchargePct: 14.5, effectiveFrom: iso(90).slice(0,10), effectiveTo: plus(270) },
  { id: 'rt-0002', tenantId: TENANT, carrierId: 'carrier-xpo', carrierName: 'XPO Logistics', scac: 'XPOL', origin: 'Chicago, IL', destination: 'Romeoville, IL', mode: 'Reefer', rate: 1380, minWeight: 20000, fuelSurchargePct: 14.5, effectiveFrom: iso(180).slice(0,10), effectiveTo: plus(180) },
  { id: 'rt-0003', tenantId: TENANT, carrierId: 'carrier-saia', carrierName: 'Saia LTL',      scac: 'SAIA', origin: 'Atlanta, GA', destination: 'Miami, FL',   mode: 'FTL',   rate: 1250, minWeight: 20000, fuelSurchargePct: 14.5, effectiveFrom: iso(60).slice(0,10), effectiveTo: plus(20) },
  { id: 'rt-0004', tenantId: TENANT, carrierId: 'carrier-estes', carrierName: 'Estes Express', scac: 'EXLA', origin: 'Houston, TX', destination: 'Los Angeles, CA', mode: 'FTL', rate: 2850, minWeight: 20000, fuelSurchargePct: 14.5, effectiveFrom: iso(120).slice(0,10), effectiveTo: plus(240) },
];

export const SEED_CARRIER_CONTRACTS = [
  { id: 'ctr-0001', tenantId: TENANT, carrierId: 'carrier-od', carrierName: 'Old Dominion', startDate: iso(90).slice(0,10), endDate: plus(270), docUrl: 'seed://od-contract.pdf', terms: 'NET30, weekly settlement' },
  { id: 'ctr-0002', tenantId: TENANT, carrierId: 'carrier-xpo', carrierName: 'XPO Logistics', startDate: iso(180).slice(0,10), endDate: plus(180), docUrl: 'seed://xpo-contract.pdf', terms: 'NET30' },
];

// ── TMS-11 Rate Confirmations + signatures ────────────────────────────
export const SEED_RATE_CONFIRMATIONS_TMS11 = [
  { id: 'rcn-0001', tenantId: TENANT, loadId: 'load-00001', quoteId: 'Q-SEED-1001', customerId: 'cust-riverside', customerName: 'Riverside Natural Foods', carrierId: 'carrier-xpo', carrierName: 'XPO Logistics', agreedRate: 1380, terms: 'NET30', status: 'signed', generatedAt: iso(12), signedAt: iso(11), signedBy: 'michelle.bero@riversidefoods.com' },
];

// ── TMS-12 LTL rate quotes + PRO tracking ─────────────────────────────
export const SEED_LTL_RATE_QUOTES = [
  {
    id: 'lrq-0001', tenantId: TENANT, customerId: 'cust-watchmen',
    origin: 'Dallas, TX', destination: 'Chicago, IL',
    weight: 2400, freightClass: 100, miles: 920,
    requestedAt: iso(0, 4), source: 'mock',
    quotes: [
      { carrierScac: 'XPOL', carrierName: 'XPO Logistics', netCharges: 412, baseCharges: 474, fuelSurcharge: 68, accessorials: [], transitDays: 3, serviceQuality: 0.87 },
      { carrierScac: 'SAIA', carrierName: 'Saia LTL',      netCharges: 438, baseCharges: 504, fuelSurcharge: 72, accessorials: [], transitDays: 3, serviceQuality: 0.90 },
      { carrierScac: 'EXLA', carrierName: 'Estes Express', netCharges: 462, baseCharges: 531, fuelSurcharge: 77, accessorials: [], transitDays: 2, serviceQuality: 0.93 },
      { carrierScac: 'ABFS', carrierName: 'ABF Freight',   netCharges: 482, baseCharges: 555, fuelSurcharge: 80, accessorials: [], transitDays: 3, serviceQuality: 0.94 },
      { carrierScac: 'ODFL', carrierName: 'Old Dominion',  netCharges: 495, baseCharges: 569, fuelSurcharge: 82, accessorials: [], transitDays: 2, serviceQuality: 0.96 },
    ],
  },
];

export const SEED_LTL_PRO_TRACKING = [
  {
    id: 'pro-0001', tenantId: TENANT, proNumber: 'XPO-7841221', carrierScac: 'XPOL',
    createdAt: iso(2), lastUpdatedAt: iso(0, 3),
    milestones: [
      { milestone: 'Picked up', occurredAt: iso(2), location: 'Dallas, TX' },
      { milestone: 'At terminal', occurredAt: iso(1), location: 'Dallas XPO Terminal' },
      { milestone: 'Departed', occurredAt: iso(1, -6), location: 'Dallas XPO Terminal' },
      { milestone: 'At terminal', occurredAt: iso(0, 12), location: 'Chicago XPO Terminal' },
      { milestone: 'Out for delivery', occurredAt: iso(0, 3), location: 'Chicago, IL' },
    ],
  },
];

export const TMS_CC_SEED_SUMMARY = {
  allocation_rules: SEED_CARRIER_ALLOCATION_RULES.length,
  report_schedules: SEED_REPORT_SCHEDULES.length,
  report_runs: SEED_REPORT_RUNS.length,
  edi_messages: SEED_EDI_MESSAGES.length,
  onboarding_apps: SEED_CARRIER_ONBOARDING_APPLICATIONS.length,
  loadboard_postings: SEED_LOADBOARD_POSTINGS.length,
  loadboard_quotes: SEED_LOADBOARD_QUOTES.length,
  rate_tables: SEED_CARRIER_RATE_TABLES.length,
  contracts: SEED_CARRIER_CONTRACTS.length,
  ratecons: SEED_RATE_CONFIRMATIONS_TMS11.length,
  ltl_quotes: SEED_LTL_RATE_QUOTES.length,
  ltl_pros: SEED_LTL_PRO_TRACKING.length,
};
